import { cn } from '@/lib/utils';

// Subtle pitch line texture for backgrounds
export const PitchBackground = ({ 
  className, 
  variant = 'subtle', // 'subtle' | 'visible' | 'hero'
  children 
}) => {
  const opacityMap = {
    subtle: 0.03,
    visible: 0.08,
    hero: 0.05
  };

  const opacity = opacityMap[variant];

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Pitch Pattern SVG */}
      <svg 
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="xMidYMid slice"
        style={{ opacity }}
      >
        <defs>
          <pattern 
            id="pitch-pattern" 
            x="0" 
            y="0" 
            width="200" 
            height="130" 
            patternUnits="userSpaceOnUse"
          >
            {/* Main rectangle (half pitch) */}
            <rect 
              x="10" 
              y="10" 
              width="180" 
              height="110" 
              fill="none" 
              stroke="currentColor"
              strokeWidth="1"
            />
            
            {/* Center line */}
            <line 
              x1="100" 
              y1="10" 
              x2="100" 
              y2="120"
              stroke="currentColor"
              strokeWidth="1"
            />
            
            {/* Center circle */}
            <circle 
              cx="100" 
              cy="65" 
              r="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
            
            {/* Penalty area left */}
            <rect 
              x="10" 
              y="30" 
              width="30" 
              height="70"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
            
            {/* Penalty area right */}
            <rect 
              x="160" 
              y="30" 
              width="30" 
              height="70"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect 
          width="100%" 
          height="100%" 
          fill="url(#pitch-pattern)" 
          className="text-foreground"
        />
      </svg>

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

// Abstract tactical motif for decorative use
export const TacticalMotif = ({ 
  className,
  variant = 'arrows' // 'arrows' | 'halfspace' | 'formation'
}) => {
  if (variant === 'arrows') {
    return (
      <svg 
        viewBox="0 0 120 40" 
        className={cn("h-8 w-auto text-accent/30", className)}
      >
        {/* Passing lane arrows */}
        <path 
          d="M10 20 L40 20 M35 15 L40 20 L35 25" 
          stroke="currentColor" 
          strokeWidth="1.5" 
          fill="none"
        />
        <path 
          d="M50 20 L80 10 M74 8 L80 10 L76 16" 
          stroke="currentColor" 
          strokeWidth="1.5" 
          fill="none"
          strokeDasharray="3 3"
        />
        <path 
          d="M50 20 L80 30 M74 32 L80 30 L76 24" 
          stroke="currentColor" 
          strokeWidth="1.5" 
          fill="none"
        />
        {/* Player dots */}
        <circle cx="10" cy="20" r="3" fill="currentColor" />
        <circle cx="50" cy="20" r="3" fill="currentColor" />
        <circle cx="85" cy="10" r="3" fill="currentColor" opacity="0.5" />
        <circle cx="85" cy="30" r="3" fill="currentColor" />
      </svg>
    );
  }

  if (variant === 'halfspace') {
    return (
      <svg 
        viewBox="0 0 100 60" 
        className={cn("h-12 w-auto", className)}
      >
        {/* Half-space rectangles */}
        <rect 
          x="5" 
          y="5" 
          width="18" 
          height="50" 
          fill="currentColor" 
          opacity="0.1"
          className="text-accent"
        />
        <rect 
          x="77" 
          y="5" 
          width="18" 
          height="50" 
          fill="currentColor" 
          opacity="0.1"
          className="text-accent"
        />
        {/* Center line */}
        <line 
          x1="50" 
          y1="5" 
          x2="50" 
          y2="55"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.3"
          className="text-border"
        />
        {/* Player in half-space */}
        <circle cx="14" cy="30" r="4" fill="currentColor" className="text-accent" />
        <circle cx="86" cy="30" r="4" fill="currentColor" className="text-accent" />
      </svg>
    );
  }

  if (variant === 'formation') {
    return (
      <svg 
        viewBox="0 0 80 50" 
        className={cn("h-10 w-auto text-muted-foreground/30", className)}
      >
        {/* Formation dots - 4-3-3 style */}
        {/* Defense */}
        <circle cx="15" cy="15" r="3" fill="currentColor" />
        <circle cx="15" cy="25" r="3" fill="currentColor" />
        <circle cx="15" cy="35" r="3" fill="currentColor" />
        
        {/* Midfield */}
        <circle cx="35" cy="10" r="3" fill="currentColor" />
        <circle cx="35" cy="25" r="3" fill="currentColor" />
        <circle cx="35" cy="40" r="3" fill="currentColor" />
        
        {/* Attack */}
        <circle cx="55" cy="15" r="3" fill="currentColor" />
        <circle cx="65" cy="25" r="3" fill="currentColor" />
        <circle cx="55" cy="35" r="3" fill="currentColor" />
        
        {/* Connecting lines */}
        <line x1="35" y1="25" x2="55" y2="15" stroke="currentColor" strokeWidth="0.5" />
        <line x1="35" y1="25" x2="65" y2="25" stroke="currentColor" strokeWidth="0.5" />
        <line x1="35" y1="25" x2="55" y2="35" stroke="currentColor" strokeWidth="0.5" />
      </svg>
    );
  }

  return null;
};

export default PitchBackground;
